import type { ChangeEvent } from 'react'
import { ChevronLeft } from 'lucide-react'
import type {
  ClaimContact,
  CompleteRegistrationFromInvitation,
  CreateSignatureRequest,
  GetSignatureRequestStatus,
  ListRequiredWaivers,
  LoadUserProfile,
  RequestNewInvite,
  SendContactClaimOtp,
  SendOtp,
  SetSession,
  SubscribeSignatureRequestStatus,
  UploadIdentityDocument,
  VerifyInvitationToken,
  VerifyOtp,
} from '@/modules/auth/use-cases'
import type {
  LoadCurrentDocument,
  RecordLegalAcceptance,
} from '@/modules/legal/use-cases'
import type { EmitFunnelEvent } from '@/shared/domain'
import { AuthLayout, OtpVerificationStep } from '@/modules/auth/ui/components'
import { formatResendLabel, formatTryAgainLabel } from '@/shared/ui/hooks'
import { RegisterLayout, RegisterProgressBar, RegisterStepShell } from '@/modules/auth/ui/register/components'
import { Button, Input } from '@/shared/ui/primitives'
import { AccountForkState } from './components/account-fork-state'
import { AccountLinkState } from './components/account-link-state'
import { AlreadyClaimedState } from './components/already-claimed-state'
import { DownloadAppState } from './components/download-app-state'
import { AlreadyRegisteredState } from './components/already-registered-state'
import { ExpiredState } from './components/expired-state'
import { InvalidState } from './components/invalid-state'
import { LoadingState } from './components/loading-state'
import { RegistrationForm } from './components/registration-form'
import { ReturningToAppState } from './components/returning-to-app-state'
import { SuccessState } from './components/success-state'
import { useJoinPage } from './hooks/use-join-page'

export type JoinPageProps = {
  onVerifyInvitationToken: VerifyInvitationToken
  onRequestNewInvite: RequestNewInvite
  onCompleteRegistrationFromInvitation: CompleteRegistrationFromInvitation
  onSendOtp: SendOtp
  onVerifyOtp: VerifyOtp
  onSendContactClaimOtp: SendContactClaimOtp
  onClaimContact: ClaimContact
  onSetSession: SetSession
  onLoadUserProfile: LoadUserProfile
  onRecordLegalAcceptance: RecordLegalAcceptance
  onLoadCurrentDocument: LoadCurrentDocument
  onListRequiredWaivers: ListRequiredWaivers
  onCreateSignatureRequest: CreateSignatureRequest
  onGetSignatureRequestStatus: GetSignatureRequestStatus
  onSubscribeSignatureRequestStatus: SubscribeSignatureRequestStatus
  onUploadIdentityDocument: UploadIdentityDocument
  onEmitFunnelEvent: EmitFunnelEvent
}

export function JoinPage(props: JoinPageProps) {
  const vm = useJoinPage(props)

  switch (vm.phase) {
    case 'verifying':
      return <LoadingState />
    case 'invalid':
      return <InvalidState message={vm.message} />
    case 'expired':
      return (
        <ExpiredState
          organizationName={vm.organizationName}
          renewable={vm.renewable}
          renewalState={vm.renewalState}
          renewalError={vm.renewalError}
          cooldownSeconds={vm.cooldownSeconds}
          onRequestNewInvite={vm.onRequestNewInvite}
        />
      )
    case 'already-registered':
      return <AlreadyRegisteredState organizationName={vm.organizationName} />
    case 'already-claimed':
      return <AlreadyClaimedState
          orgName={vm.orgName}
          role={vm.role}
          membershipModel={vm.membershipModel}
        />
    case 'account-fork':
      return (
        <AccountForkState
          inviteEmail={vm.inviteEmail}
          organizationName={vm.organizationName}
          yesEmail={vm.yesEmail}
          isSubmitting={vm.isSubmitting}
          cooldownSeconds={vm.cooldownSeconds}
          onYesEmailChange={vm.onYesEmailChange}
          onChooseNo={vm.onChooseNo}
          onChooseYes={vm.onChooseYes}
        />
      )
    case 'account-link':
      return (
        <AccountLinkState
          inviteEmail={vm.inviteEmail}
          linkEmail={vm.linkEmail}
          isSubmitting={vm.isSubmitting}
          cooldownSeconds={vm.cooldownSeconds}
          onLinkEmailChange={vm.onLinkEmailChange}
          onSendCode={vm.onSendCode}
          onCancel={vm.onCancel}
        />
      )
    case 'hijack-warning':
      return (
        <AuthLayout
          title="You're already signed in"
          subtitle={`This invitation is for ${vm.inviteEmail}. You're currently signed in as ${vm.existingUserEmail}.`}
        >
          <div className="flex flex-col gap-3">
            <Button type="button" variant="default" onClick={vm.onSignOutAndContinue}>
              Sign out and continue
            </Button>
            <Button type="button" variant="outline" onClick={vm.onCancel}>
              Cancel
            </Button>
          </div>
        </AuthLayout>
      )
    case 'identity-conflict':
      return (
        <AuthLayout
          title="This invite belongs to an existing account"
          subtitle={
            vm.channel === 'phone'
              ? `The matching account signs in with the phone number ${vm.maskedIdentifier}. Sign in to accept this invitation.`
              : `The matching account signs in as ${vm.maskedIdentifier}. Sign in to accept this invitation.`
          }
        >
          <div className="flex flex-col gap-3">
            <Button type="button" variant="default" onClick={vm.onSignIn}>
              Sign in to continue
            </Button>
          </div>
        </AuthLayout>
      )
    case 'contact-conflict':
      return (
        <AuthLayout
          title={
            vm.channel === 'phone'
              ? 'This phone number is tied to another BlindBook account'
              : 'This email is tied to another BlindBook account'
          }
          subtitle={
            !vm.canChallenge
              ? vm.channel === 'phone'
                ? `${vm.identifier} is already in use. Use a different phone number, or contact BlindBook support to merge the accounts.`
                : `${vm.identifier} is already in use. Use a different email, or contact BlindBook support to merge the accounts.`
              : vm.channel === 'phone'
                ? `If ${vm.identifier} is your number, we can text you a code to connect this invitation to your existing account.`
                : `If ${vm.identifier} is your email, we can send you a code to connect this invitation to your existing account.`
          }
        >
          {vm.canChallenge ? (
            <div className="flex flex-col gap-3">
              <Button
                type="button"
                variant="default"
                onClick={vm.onChallenge}
                disabled={vm.isChallengeSending || vm.challengeCooldownSeconds > 0}
              >
                {vm.challengeCooldownSeconds > 0
                  ? formatTryAgainLabel(vm.challengeCooldownSeconds)
                  : vm.channel === 'phone'
                    ? 'Text me a code'
                    : 'Email me a code'}
              </Button>
              <Button type="button" variant="outline" onClick={vm.onEditInfo}>
                I mistyped — edit my info
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <Button asChild variant="outline">
                <a href={vm.supportHref}>Contact support</a>
              </Button>
              <Button type="button" variant="default" onClick={vm.onEditInfo}>
                {vm.channel === 'phone' ? 'Use a different number' : 'Use a different email'}
              </Button>
            </div>
          )}
        </AuthLayout>
      )
    case 'contact-claim':
      return (
        <AuthLayout
          title={
            vm.identifierType === 'email'
              ? 'Verify this email to claim it'
              : 'Verify this phone number to claim it'
          }
          subtitle={
            vm.pendingInvite
              ? `${vm.identifier} is tied to a pending invite. Verify it to claim your profile.`
              : vm.holderUnregistered
                ? vm.identifierType === 'email'
                  ? 'This email belongs to an unfinished signup. Verify it to claim it as yours.'
                  : 'This phone number belongs to an unfinished signup. Verify it to claim it as yours.'
                : `${vm.identifier} is on another account but unverified. Verify it to claim it as yours.`
          }
        >
          <div
            className="flex flex-col gap-2 rounded-lg border bg-card p-3"
            data-testid="contact-claim-panel"
          >
            <p className="text-sm">
              {vm.codeSent
                ? `Enter the code we sent to ${vm.identifier}.`
                : `We'll send a verification code to ${vm.identifier}.`}
            </p>
            {vm.codeSent ? (
              <div className="flex items-center gap-2">
                <Input
                  id="contact-claim-code"
                  inputMode="numeric"
                  value={vm.claimCode}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    vm.onClaimCodeChange(e.target.value)
                  }
                  placeholder="Enter code"
                  aria-label="Verification code"
                />
                <Button
                  type="button"
                  onClick={vm.onConfirmClaim}
                  disabled={vm.isClaiming || vm.claimCode.trim() === ''}
                >
                  {vm.isClaiming ? 'Verifying…' : 'Verify & claim'}
                </Button>
              </div>
            ) : null}
            <div className="flex items-center justify-end gap-2">
              <Button type="button" variant="outline" onClick={vm.onCancelClaim}>
                Use a different {vm.identifierType}
              </Button>
              <Button
                type="button"
                onClick={vm.onSendClaimCode}
                disabled={vm.isClaiming || vm.isSendingClaimCode || vm.resendSeconds > 0}
              >
                {vm.resendSeconds > 0
                  ? formatResendLabel(vm.resendSeconds)
                  : vm.codeSent
                    ? 'Resend code'
                    : 'Send me a code'}
              </Button>
            </div>
            {vm.claimError ? (
              <p role="alert" className="text-xs text-destructive">
                {vm.claimError}
              </p>
            ) : null}
          </div>
        </AuthLayout>
      )
    case 'otp':
      return (
        <OtpVerificationStep
          identifier={vm.identifier}
          channel={vm.channel}
          onSendOtp={vm.onSendOtp}
          onVerifyOtp={vm.onVerifyOtp}
          onVerifySuccess={vm.onVerifySuccess}
          onBack={vm.onBack}
          variant="standalone"
        />
      )
    case 'success':
      return <SuccessState organizationName={vm.organizationName} onSignOut={vm.onSignOut} />
    case 'download-app':
      return <DownloadAppState organizationName={vm.organizationName} />
    case 'returning-to-app':
      return <ReturningToAppState organizationName={vm.organizationName} />
    case 'form':
      return (
        <RegisterLayout scrollRef={vm.scrollContainerRef}>
          <header className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b px-6 py-4">
            <img
              src="/blindbookStackedLogo.svg"
              alt="BlindBook"
              className="order-1 ml-2 h-12 w-auto"
            />
            <span className="order-2 ml-auto text-lg text-muted-foreground md:order-3">
              {vm.organization.name}
            </span>
            {vm.canGoBack ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={vm.goBack}
                aria-label="Go to previous step"
                className="order-3 basis-full justify-start md:order-2 md:basis-auto"
              >
                <ChevronLeft className="size-4" />
                Back
              </Button>
            ) : null}
          </header>

          <RegisterProgressBar
            currentStep={vm.currentStepIndex + 1}
            totalSteps={vm.steps.length}
          />

          <RegisterStepShell>
            <RegistrationForm
              organization={vm.organization}
              values={vm.values}
              errors={vm.errors}
              isSubmitting={vm.isSubmitting}
              isMinor={vm.isMinor}
              age={vm.age}
              emailLocked={vm.emailLocked}
              phoneRequired={vm.phoneRequired}
              phoneLocked={vm.phoneLocked}
              canLinkExistingAccount={vm.canLinkExistingAccount}
              linkedEmail={vm.linkedEmail}
              onLinkExistingAccount={vm.onLinkExistingAccount}
              tosAccepted={vm.tosAccepted}
              privacyAccepted={vm.privacyAccepted}
              legalAlreadyAccepted={vm.legalAlreadyAccepted}
              steps={vm.steps}
              currentStepIndex={vm.currentStepIndex}
              currentStep={vm.currentStep}
              isLastStep={vm.isLastStep}
              isStepSkippable={vm.isStepSkippable}
              canGoBack={vm.canGoBack}
              isCurrentStepComplete={vm.isCurrentStepComplete}
              goBack={vm.goBack}
              goNext={vm.goNext}
              onSkip={vm.onSkip}
              licenseEntries={vm.licenseEntries}
              driversLicense={vm.driversLicense}
              onDriversLicenseChange={vm.onDriversLicenseChange}
              onFieldChange={vm.onFieldChange}
              onLicenseChange={vm.onLicenseChange}
              onTosChange={vm.onTosChange}
              onPrivacyChange={vm.onPrivacyChange}
              onSubmit={vm.onSubmit}
              onLoadCurrentDocument={vm.onLoadCurrentDocument}
              onDocumentsLoaded={vm.onDocumentsLoaded}
              waiver={vm.waiver}
            />
          </RegisterStepShell>
        </RegisterLayout>
      )
  }
}
